import React from 'react';

import {
    ChevronLeftRounded,
    AccountCircle,
    ChevronRightRounded,
    InsertDriveFile,
    LocationOn,
    Phone,
    OpenInNew,
    Reply,
} from '@mui/icons-material';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';

import {
    MergeTag,
    WhatsAppComponent,
    isBodyComponent,
    isButtonsComponent,
    isCarouselComponent,
    isFooterComponent,
    isHeaderComponent,
    isLTOComponent,
    TemplateComponentButtonType,
    TemplateHeaderFormat,
} from 'domains/content/types';
import { getFriendlyName } from 'utils';

import {
    messageAreaStyles,
    messageBubbleStyles,
    messageDescriptorStyles,
    messageTextStyles,
    previewContainerStyles,
    previewHeaderStyles,
    receivedMessageBubbleStyles,
} from './styles';

// ─── Merge tag replacement ────────────────────────────────────────────────────

const applyMergeTags = (text: string, mergeTags: MergeTag[]): string => {
    if (!text || !mergeTags.length) return text;
    const regex = /{{.*?}}/g;
    return text.replace(regex, (match) => {
        const tag = mergeTags.find((t) => t.value === match);
        return tag ? tag.previewValue || `[${getFriendlyName(tag.name)}]` : match;
    });
};

// ─── WhatsApp markdown renderer ───────────────────────────────────────────────

export const renderWhatsAppMarkdown = (text: string): React.ReactNode => {
    if (!text) return null;

    // Split on markdown tokens: *bold*, _italic_, ~strike~, ```mono```
    const parts = text.split(/(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|```[^`]+```)/g);

    return parts.map((part, i) => {
        if (/^\*[^*\n]+\*$/.test(part)) {
            return <strong key={i}>{part.slice(1, -1)}</strong>;
        }
        if (/^_[^_\n]+_$/.test(part)) {
            return <em key={i}>{part.slice(1, -1)}</em>;
        }
        if (/^~[^~\n]+~$/.test(part)) {
            return <s key={i}>{part.slice(1, -1)}</s>;
        }
        if (/^```[^`]+```$/.test(part)) {
            return (
                <code
                    key={i}
                    style={{
                        fontFamily: 'monospace',
                        backgroundColor: 'rgba(0,0,0,0.06)',
                        borderRadius: 3,
                        padding: '1px 4px',
                        fontSize: '0.85em',
                    }}
                >
                    {part.slice(3, -3)}
                </code>
            );
        }
        // Plain text — preserve line breaks
        return part.split('\n').reduce<React.ReactNode[]>((acc, line, j, arr) => {
            acc.push(<span key={`${i}-${j}`}>{line}</span>);
            if (j < arr.length - 1) acc.push(<br key={`${i}-br-${j}`} />);
            return acc;
        }, []);
    });
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const PreviewText = ({ text }: { text: string }) => (
    <Typography variant="body2" component="div" sx={{ ...messageTextStyles }}>
        {renderWhatsAppMarkdown(text)}
    </Typography>
);

const PreviewHeader = ({
    component,
    mergeTags,
}: {
    component: ReturnType<typeof isHeaderComponent> extends true ? never : Parameters<typeof isHeaderComponent>[0];
    mergeTags: MergeTag[];
}) => {
    if (!isHeaderComponent(component)) return null;

    if (component.format === TemplateHeaderFormat.IMAGE) {
        return component.url ? (
            <Box
                component="img"
                src={component.url}
                alt="Header image"
                sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: '12px 12px 0 0' }}
                data-testid="preview-header-image"
            />
        ) : (
            <Box
                sx={{
                    width: '100%',
                    height: 100,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px 12px 0 0',
                }}
                data-testid="preview-header-image-placeholder"
            >
                <Typography variant="caption" color="text.secondary">
                    Image
                </Typography>
            </Box>
        );
    }

    if (component.format === TemplateHeaderFormat.VIDEO) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: 100,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px 12px 0 0',
                }}
                data-testid="preview-header-video"
            >
                <Typography variant="caption" color="text.secondary">
                    ▶ Video
                </Typography>
                {component.url && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }} noWrap>
                        {component.url}
                    </Typography>
                )}
            </Box>
        );
    }

    if (component.format === TemplateHeaderFormat.DOCUMENT) {
        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: '12px 12px 0 0' }}
                data-testid="preview-header-document"
            >
                <InsertDriveFile fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" noWrap>
                    {component.url ? component.url.split('/').pop() : 'Document'}
                </Typography>
            </Stack>
        );
    }

    if (component.format === TemplateHeaderFormat.LOCATION) {
        return (
            <Stack
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: '12px 12px 0 0' }}
                data-testid="preview-header-location"
            >
                <LocationOn fontSize="small" color="error" />
                <Stack>
                    {component.location?.name && (
                        <Typography variant="caption" fontWeight={600}>
                            {component.location.name}
                        </Typography>
                    )}
                    {component.location?.address && (
                        <Typography variant="caption" color="text.secondary">
                            {component.location.address}
                        </Typography>
                    )}
                    {!component.location?.name && !component.location?.address && (
                        <Typography variant="caption" color="text.secondary">
                            Location
                        </Typography>
                    )}
                </Stack>
            </Stack>
        );
    }

    // TEXT format
    const text = applyMergeTags(component.text ?? '', mergeTags);
    return text ? <PreviewText text={text} /> : null;
};

const PreviewButtons = ({ component }: { component: Parameters<typeof isButtonsComponent>[0] }) => {
    if (!isButtonsComponent(component)) return null;
    return (
        <Stack spacing={0.5} mt={0.5} data-testid="preview-buttons">
            {component.buttons.map((btn, i) => {
                const icon =
                    btn.type === TemplateComponentButtonType.PHONE_NUMBER ? (
                        <Phone sx={{ fontSize: 14 }} />
                    ) : btn.type === TemplateComponentButtonType.URL ? (
                        <OpenInNew sx={{ fontSize: 14 }} />
                    ) : btn.type === TemplateComponentButtonType.QUICK_REPLY ? (
                        <Reply sx={{ fontSize: 14 }} />
                    ) : null;
                const label =
                    btn.type === TemplateComponentButtonType.COPY_CODE
                        ? 'Copy Code'
                        : btn.type === TemplateComponentButtonType.COPY_OFFER_CODE
                        ? `Copy offer code`
                        : 'text' in btn
                        ? btn.text
                        : '';

                return (
                    <Divider key={i}>
                        <Chip
                            data-testid={`preview-button-${i}`}
                            icon={icon ?? undefined}
                            label={label || `Button ${i + 1}`}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1, fontSize: '0.7rem', color: '#00A5F4', borderColor: '#b3e0fa' }}
                        />
                    </Divider>
                );
            })}
        </Stack>
    );
};

const PreviewCarousel = ({
    component,
    mergeTags,
}: {
    component: Parameters<typeof isCarouselComponent>[0];
    mergeTags: MergeTag[];
}) => {
    if (!isCarouselComponent(component)) return null;
    return (
        <Box data-testid="preview-carousel">
            <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
                {component.cards.map((card, i) => (
                    <Box
                        key={i}
                        sx={{
                            minWidth: 180,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            flexShrink: 0,
                        }}
                        data-testid={`preview-carousel-card-${i}`}
                    >
                        <Box
                            sx={{
                                height: 80,
                                bgcolor: 'action.hover',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {card.header_format === TemplateHeaderFormat.VIDEO ? '▶ Video' : 'Image'}
                            </Typography>
                        </Box>
                        <Box sx={{ p: 1 }}>
                            <Typography variant="caption" sx={{ ...messageTextStyles, fontSize: '0.7rem' }}>
                                {renderWhatsAppMarkdown(applyMergeTags(card.body_text, mergeTags)) || (
                                    <em style={{ color: '#999' }}>Card body text</em>
                                )}
                            </Typography>
                        </Box>
                        {card.buttons.map((btn, bi) => (
                            <Divider key={bi}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#00A5F4' }}>
                                    {'text' in btn ? btn.text : 'Button'}
                                </Typography>
                            </Divider>
                        ))}
                    </Box>
                ))}
            </Stack>
        </Box>
    );
};

const PreviewLTO = ({ component }: { component: Parameters<typeof isLTOComponent>[0] }) => {
    if (!isLTOComponent(component)) return null;
    const { text, has_expiration } = component.limited_time_offer;
    return (
        <Box sx={{ bgcolor: '#FFF3E0', borderRadius: 1, p: 1, mb: 0.5 }} data-testid="preview-lto">
            <Typography variant="caption" fontWeight={700} color="warning.dark">
                {text || 'Offer title'}
            </Typography>
            {has_expiration && (
                <Typography variant="caption" display="block" color="text.secondary">
                    Expires in: --:--:--
                </Typography>
            )}
        </Box>
    );
};

// ─── Main preview ─────────────────────────────────────────────────────────────

export type Props = {
    /** Legacy: pass formatted body string for old (non-flagged) builder */
    formattedContent?: string;
    /** New builder: pass full components array */
    components?: WhatsAppComponent[];
    mergeTags?: MergeTag[];
};

const WhatsAppPreview = ({ formattedContent, components, mergeTags = [] }: Props) => {
    const hasComponents = components && components.length > 0;

    const headerComponent = hasComponents ? components.find(isHeaderComponent) : null;
    const bodyComponent = hasComponents ? components.find(isBodyComponent) : null;
    const footerComponent = hasComponents ? components.find(isFooterComponent) : null;
    const buttonsComponent = hasComponents ? components.find(isButtonsComponent) : null;
    const carouselComponent = hasComponents ? components.find(isCarouselComponent) : null;
    const ltoComponent = hasComponents ? components.find(isLTOComponent) : null;

    const bodyText = bodyComponent ? applyMergeTags(bodyComponent.text, mergeTags) : formattedContent ?? '';

    const showPlaceholder = !hasComponents && !formattedContent;

    return (
        <Stack
            height="auto"
            justifySelf="center"
            sx={{ width: '100%', maxWidth: '320px', maxHeight: 'calc(100vh - 250px)' }}
            data-testid="whatsapp-preview"
        >
            <Stack sx={previewContainerStyles}>
                {/* Phone header — dark teal like real WhatsApp */}
                <Stack direction="row" alignItems="center" sx={previewHeaderStyles}>
                    <ChevronLeftRounded fontSize="large" htmlColor="rgba(255,255,255,0.8)" />
                    <Box flex={1} ml={-5} display="flex" justifyContent="center">
                        <Stack alignItems="center">
                            <AccountCircle fontSize="large" htmlColor="rgba(255,255,255,0.9)" />
                            <Stack direction="row" alignItems="center">
                                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
                                    +1 (999) 999-9999
                                </Typography>
                                <ChevronRightRounded fontSize="small" htmlColor="rgba(255,255,255,0.7)" />
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>

                {/* Message area */}
                <Stack sx={messageAreaStyles}>
                    <Typography variant="overline" sx={messageDescriptorStyles}>
                        WhatsApp Message
                        <br />
                        {DateTime.now().toFormat("ccc, MMM d 'at' h:mm a")}
                    </Typography>

                    {showPlaceholder ? (
                        <Box sx={{ ...messageBubbleStyles, ...receivedMessageBubbleStyles }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}
                            >
                                Start typing to see a live preview of your message here!
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', mb: 1, justifyContent: 'flex-start' }}>
                            <Box
                                sx={{
                                    ...messageBubbleStyles,
                                    ...receivedMessageBubbleStyles,
                                    p: 0,
                                    overflow: 'hidden',
                                    maxWidth: '90%',
                                }}
                            >
                                {/* Header */}
                                {headerComponent && <PreviewHeader component={headerComponent} mergeTags={mergeTags} />}

                                {/* LTO */}
                                {ltoComponent && (
                                    <Box sx={{ px: 1.5, pt: 1 }}>
                                        <PreviewLTO component={ltoComponent} />
                                    </Box>
                                )}

                                {/* Carousel */}
                                {carouselComponent && (
                                    <Box sx={{ px: 1, pt: 1 }}>
                                        <PreviewCarousel component={carouselComponent} mergeTags={mergeTags} />
                                    </Box>
                                )}

                                {/* Body */}
                                {(bodyText || hasComponents) && (
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            pt: headerComponent || ltoComponent || carouselComponent ? 1 : 1.5,
                                            pb: 0.5,
                                        }}
                                    >
                                        {bodyText ? (
                                            <PreviewText text={bodyText} />
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                sx={{ fontStyle: 'italic', color: 'text.disabled', fontSize: '0.8rem' }}
                                            >
                                                Body text…
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {/* Footer */}
                                {footerComponent && (
                                    <Box sx={{ px: 1.5, pb: 1 }}>
                                        <Typography variant="caption" color="text.disabled">
                                            {footerComponent.text}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Buttons */}
                                {buttonsComponent && (
                                    <Box sx={{ px: 0.5, pb: 1 }}>
                                        <PreviewButtons component={buttonsComponent} />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

export default React.memo(WhatsAppPreview);
